import { useEffect, useRef, useState } from 'react'

import {
  fetchSpartacusStats,
  fetchSpartacusVenues,
} from 'src/utils/spartacusApi'
import {
  mapSpartacusStatsResponse,
  mapSpartacusVenuesResponse,
} from 'src/utils/spartacusMappers'

const EMPTY_VENUES_STATE = {
  venues: [],
  pagination: {
    page: 1,
    pageSize: 25,
    total: 0,
  },
  meta: null,
}

export function useSpartacusData({ statsQueryParams, venuesQueryParams }) {
  const [venuesState, setVenuesState] = useState(EMPTY_VENUES_STATE)
  const [stats, setStats] = useState(null)
  const [venuesLoading, setVenuesLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [venuesError, setVenuesError] = useState(null)
  const [statsError, setStatsError] = useState(null)
  const hasLoadedStatsRef = useRef(false)
  const hasLoadedVenuesRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    async function loadStats() {
      setStatsLoading(true)
      setStatsError(null)

      try {
        const data = await fetchSpartacusStats(statsQueryParams)
        if (!cancelled) {
          setStats(mapSpartacusStatsResponse(data))
          hasLoadedStatsRef.current = true
        }
      } catch (error) {
        if (!cancelled) {
          setStatsError(error.message || 'Failed to load Spartacus stats')
          if (!hasLoadedStatsRef.current) {
            setStats(null)
          }
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false)
        }
      }
    }

    loadStats()

    return () => {
      cancelled = true
    }
  }, [statsQueryParams])

  useEffect(() => {
    let cancelled = false

    async function loadVenues() {
      setVenuesLoading(true)
      setVenuesError(null)

      try {
        const data = await fetchSpartacusVenues(venuesQueryParams)
        if (!cancelled) {
          const mapped = mapSpartacusVenuesResponse(data)
          setVenuesState({
            venues: mapped.venues,
            pagination: mapped.pagination,
            meta: mapped.meta,
          })
          hasLoadedVenuesRef.current = true
        }
      } catch (error) {
        if (!cancelled) {
          setVenuesError(error.message || 'Failed to load Spartacus venues')
          if (!hasLoadedVenuesRef.current) {
            setVenuesState(EMPTY_VENUES_STATE)
          }
        }
      } finally {
        if (!cancelled) {
          setVenuesLoading(false)
        }
      }
    }

    loadVenues()

    return () => {
      cancelled = true
    }
  }, [venuesQueryParams])

  return {
    venues: venuesState.venues,
    pagination: venuesState.pagination,
    venuesMeta: venuesState.meta,
    stats,
    statsMeta: stats?.meta || null,
    venuesLoading,
    statsLoading,
    venuesError,
    statsError,
  }
}
